// React import omitted (not directly used with TSX in this setup)
import { useTranslation } from 'react-i18next';
import { Button, Icon, Page, Paper } from '@insio/ui-toolkit';
import { Link } from 'react-router-dom';
import { navigation } from '../../../navigation.ts';

export type ErrorPageMode = 'boundary' | 'notFound';

export interface ErrorPageProps {
  mode: ErrorPageMode;
  error?: Error | null;
  errorInfo?: { componentStack?: string } | null;
  onReload?: () => void;
  onGoHome?: () => void; // optional override
  className?: string;
}

/**
 * Unified error page component handling both runtime error boundary fallback and 404 Not Found.
 * Constitution alignment: single responsibility + centralized UI for error states.
 */
export function ErrorPage({
  mode,
  error,
  errorInfo,
  onReload,
  onGoHome,
  className
}: ErrorPageProps) {
  const { t } = useTranslation();
  const isBoundary = mode === 'boundary';

  const title = isBoundary ? t('error.boundary.title') : t('error.notFound.title');
  const message = isBoundary ? t('error.boundary.message') : t('error.notFound.message');

  return (
    <Page className={className}>
      <Page.Content>
        <div className='flex items-center justify-center min-h-[60vh]'>
          <Paper>
            <Paper.Content>
              <div className='flex flex-col items-center gap-lg py-xl px-md text-center'>
                <div className='text-foreground-disabled'>
                  <Icon name={isBoundary ? 'triangle-exclamation' : 'circle-xmark'} size='2xl' />
                </div>

                <h1 className='text-2xl font-semibold text-foreground-base'>{title}</h1>
                <p className='text-foreground-muted max-w-md'>{message}</p>

                {isBoundary && error && (
                  <details className='mt-sm w-full'>
                    <summary className='cursor-pointer text-sm font-medium text-foreground-muted hover:text-foreground-base'>
                      {t('error.boundary.details')}
                    </summary>
                    <div className='mt-sm p-md bg-error-bg border border-error-border rounded-md text-left w-full'>
                      <pre className='text-xs text-error-text overflow-auto whitespace-pre-wrap'>
                        {error.toString()}
                        {errorInfo?.componentStack && '\n\n' + errorInfo.componentStack}
                      </pre>
                    </div>
                  </details>
                )}

                <div className='flex flex-wrap gap-md mt-md justify-center'>
                  {isBoundary && onReload && (
                    <Button variant='primary' onClick={onReload}>
                      {t('error.boundary.reload')}
                    </Button>
                  )}
                  <Button
                    variant='ghost'
                    onClick={onGoHome || (() => (window.location.href = navigation.dashboard()))}
                  >
                    <Icon name='house' /> {t('error.notFound.goHome')}
                  </Button>
                  {!isBoundary && (
                    <Link to={navigation.dashboard()}>
                      <Button variant='primary'>
                        <Icon name='briefcase' /> {t('error.notFound.goDashboard')}
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            </Paper.Content>
          </Paper>
        </div>
      </Page.Content>
    </Page>
  );
}

export default ErrorPage;
